import { Formik } from "formik";
import { Box } from "@mui/system";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import MUIDataTable from "mui-datatables";
import { useLocation } from "react-router";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import { configState } from "../../../recoil/atoms";
import CalendarScheduler from "../Calendar/Calendar";
import { useNavigate, useParams } from "react-router";
import LinearProgress from "@mui/material/LinearProgress";
import { IconCalendarPlus, IconChecks } from "@tabler/icons";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../utils/FormFields/ConfirmDialog";
import useAuthorization from "../../Authentication/permissions/Authorization";
import AutocompletePatient from "../Appointments/FormFields/AutocompletePatient";
import { Button, ButtonGroup, CircularProgress, Grid, Paper } from "@mui/material";
import { AppointmentApi, EditAppointmentRequest, EditAppointmentRequestStatusEnum, GetAppointmentsResponseAppointments } from "../../../api/medical-operations";

const moment = require('moment-timezone');

interface GetAppointmentParams {  
  offset?: number;
  limit?: number;
}

export default function Appointments() {
  const navigate = useNavigate();
  let { patientID } = useParams();
  const { pathname } = useLocation();
  const config = useRecoilValue(configState);
  const isSpecificPatientAppointments = pathname.includes("patient");

  const [count, setCount] = useState<number>();
  const [rowID, setRowID] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [calendarView, setCalendarView] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean[]>([]);
  const [selectedPatientID, setSelectedPatientID] = useState<string | undefined>(undefined);
  const [appointments, setAppointments] = useState<GetAppointmentsResponseAppointments[]>([]);

  const { canGetAppointments, canEditAppointment, canScheduleAppointment, canDeleteAppointment } = useAuthorization();
  
  const getAppointments = (params: GetAppointmentParams = {}) => {
    const {      
      offset = 0,
      limit = 10,
    } = params;

    const appointment = new AppointmentApi(config);
    appointment
      .getAppointments(patientID || selectedPatientID, undefined, undefined, offset, limit)
      .then((res) => {        
        setAppointments(res.data.appointments!);
        setButtonLoading(Array(res.data.appointments?.length || 0).fill(false));
        setCount(res.data.total); 
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(()=>{
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientID]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deleteAppointment(rowID);
    handleCloseDialog();
  };

  const deleteAppointment = (id: string) => {
    const Appointment = new AppointmentApi(config);
    Appointment.deleteAppointment(id)
      .then((res) => {
        getAppointments();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };  

  const editAppointment = (appointmentID: string, values: EditAppointmentRequest) => {
    const Appointment = new AppointmentApi(config);
    Appointment.editAppointment(appointmentID, values)
      .then((res) => {
        toast.success("Appointment edited");
        getAppointments();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };
  
  const closeStatus = (appointmentID: string, values: EditAppointmentRequest) => {
    const updatedValues: EditAppointmentRequest = {
      ...values,
      Status: EditAppointmentRequestStatusEnum.Closed,
    };
    const now = moment();
    if (values.DateFrom && moment(values.DateFrom).isAfter(now)) {
      toast.error("Can't set 'Closed'  status for future appointments");
      return;
    }
    editAppointment(appointmentID, updatedValues);
  };  

  const handlePagination = (tableState: any) => {
    const { page, rowsPerPage } = tableState;        

    getAppointments({
      offset: page * rowsPerPage,
      limit: rowsPerPage      
    });
  };

  const columns = [
    {
      name: "AppointmentID",
      options: {
        display: false,
        filter: false,
        download: false,
        print: false,
      },
    },
    {
      name: "PatientFirstName",
      label: "Patient First Name",
    },
    {
      name: "PatientFamilyName",
      label: "Patient Last Name",
    },
    {
      name: "ConsultationReason",
      label: "Consultation Reason",
    },

    {
      name: "Priority",
      label: "Priority",
    },
    {
      name: "DateFrom",
      label: "Date From",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          const date = new Date(value);
          return date.toLocaleString("en-GB", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZone: "UTC",
          });
        },
      },
    },
    {
      name: "DateTo",
      label: "Date To",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          const date = new Date(value);
          return date.toLocaleString("en-GB", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZone: "UTC",
          });
        },
      },
    },
    {
      name: "Status",
      label: "Status",
    },
    {
      name: "Priority",
      label: "Priority",
      options: {
        display: false,
      },
    },
    {
      name: "Notes",
      label: "Notes",
      options: {
        display: false,
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {          
          const status = tableMeta.rowData[7];
          const isNotEditable = status === "Canceled" || status === "Missed";

          return (
          <>
            {canEditAppointment && !isNotEditable && (
              <Tooltip title="Edit">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    navigate(`/appointment/${tableMeta.rowData[0]}/info`, {
                      state: tableMeta.tableData[tableMeta.rowIndex],
                    });
                  }}
                >
                  <EditIcon color="secondary" />
                </Button>
              </Tooltip>
            )}
            {canDeleteAppointment && (
              <Tooltip title="Delete">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    handleDelete(tableMeta.rowData[0]);
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </Button>
              </Tooltip>
            )}
            {canEditAppointment && !isNotEditable && (
              <Tooltip title="Close Appointment">
                {!buttonLoading[tableMeta.rowIndex] ? (
                  <Button
                    variant="contained"
                    color={tableMeta.rowData[7] === "Closed" ? "inherit" : "success"}
                    sx={{
                      ml: { xs: 0, xl: 2 },
                    }}
                    disabled={tableMeta.rowData[7] === "Closed" || buttonLoading[tableMeta.rowIndex]}
                    onClick={() => {
                      setButtonLoading((prevState): any =>
                        prevState.map((item, idx) => (idx === tableMeta.rowIndex ? !item : item))
                      );
                      const appointmentID = tableMeta.rowData[0];
                      const values: EditAppointmentRequest = {
                        ConsultationReason: tableMeta.rowData[3],
                        DateTo: tableMeta.rowData[6],
                        Notes: tableMeta.rowData[9],
                        Priority: tableMeta.rowData[8],
                        DateFrom: tableMeta.rowData[5],
                      };
                      closeStatus(appointmentID, values);
                    }}
                  >
                    <IconChecks color={tableMeta.rowData[8] === "Closed" ? "grey" : "green"} />
                  </Button>
                ) : (
                  <CircularProgress
                    color="secondary"
                    size={14}
                    sx={{
                      ml: { xs: 0, xl: 5 },
                    }}
                  />
                )}
              </Tooltip>
            )}
          </>
        )},
      },
    },
  ];
  
  return (
    <>
      {canGetAppointments && (
        <MainCard>
          {!isLoading ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <ButtonGroup color="secondary" variant="outlined" aria-label="outlined primary button group">
                  <Button disabled={!calendarView} color="secondary" onClick={() => setCalendarView(false)}>
                    Table View
                  </Button>
                  <Button disabled={calendarView} color="secondary" onClick={() => setCalendarView(true)}>
                    Calendar View
                  </Button>
                </ButtonGroup>
              </Box>
              {calendarView ? (
                <Paper>
                  <CalendarScheduler appointments={appointments} />
                </Paper>
              ) : (
                <Grid>
                  <>
                    <Formik
                      initialValues={{}}
                      enableReinitialize={true}                    
                      onSubmit={() => {}}
                    >
                      {!isSpecificPatientAppointments && (
                        <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                          <AutocompletePatient 
                            name="patient_id"
                            setSelectedPatientID={setSelectedPatientID}
                          />
                        </Grid>
                      )}
                    </Formik>
                    <MUIDataTable
                      data={appointments}
                      columns={columns}
                      options={{
                        serverSide: true,
                        search: false,
                        count: count,
                        filter: false,
                        viewColumns: false,
                        selectableRows: "none",
                        selectableRowsOnClick: false,
                        elevation: 0,
                        rowsPerPageOptions : [10,30,50,100], 
                        filterType: "dropdown",
                        enableNestedDataAccess: ".",
                        responsive: "standard",
                        onTableChange: async (action, tableMeta) => {                                                                                                                                           
                          switch (action) {
                            case "changePage":
                              handlePagination(tableMeta);
                              break;
                            case "changeRowsPerPage":
                              handlePagination(tableMeta);
                              break;
                            default:                          
                              break;
                          }                   
                        },  
                        customToolbar: () => {
                          return (
                            canScheduleAppointment && (
                              <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<IconCalendarPlus />}
                                onClick={() => {
                                  const url = patientID ? `/add-appointment/${patientID}` : "/appointment/add";
                                  navigate(url, {
                                    state: ""
                                  });
                                }}                                
                                sx={{
                                  ml: "50px",
                                }}
                              >
                                Add Appointment
                              </Button>
                            )
                          );
                        },
                      }}
                      title=""
                    />
                    <ConfirmDialog
                      name={"appointment"}
                      handleCloseDialog={handleCloseDialog}
                      handleConfirm={handleConfirm}
                      openDialog={openDialog}
                    />
                  </>
                </Grid>
              )}
            </>
          ) : (
            <LinearProgress color="secondary" />
          )}
        </MainCard>
      )}
    </>
  );
}
